import { useState, useEffect } from 'react';

/**
 * Default debounce delay in milliseconds
 * Commonly used for search inputs and form fields
 */
export const DEFAULT_DEBOUNCE_DELAY = 300;

/**
 * Custom hook to debounce a value
 * Useful for search inputs and other frequently changing values
 *
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 300ms)
 * @returns The debounced value
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearch = useDebounce(searchTerm);
 * // Use debouncedSearch for API calls or filtering
 */
export function useDebounce<T>(value: T, delay: number = DEFAULT_DEBOUNCE_DELAY): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
